import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { Button, Dialog, DialogTrigger, GridList, GridListItem, Popover } from "react-aria-components";
import { FaVideo } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { VStack } from "../../../../styled-system/jsx";
import { useGetAiVideoGenRequests } from "../../../modules/perf_reports/api/assets/useGetAiVideoGenTaskResults";
import moment from "moment";
import { TIME_TO_REGISTER_ASSETS } from "./VideoGenerationRequestsTable";
import { useDateFormatter } from "../../../i18n/useDateFormatter";

export const CompletedVideoGenerationRequestsWidget = () => {
  const { t } = useTranslation(["advertising"]);
  const dateFmt = useDateFormatter("medium");
  const completedSince = useMemo(
    () => moment.utc().subtract(TIME_TO_REGISTER_ASSETS, "hours").startOf("minute").toISOString(),
    [],
  );

  const filters = {
    state: ["SUCCESS"],
    completed_at__isnull: false,
    completed_at__gte: completedSince,
    registered: false,
    reviewed_at__isnull: true,
  };

  const completedVideoGenerationResultsQuery = useGetAiVideoGenRequests(filters);

  const completedVideoGenerationRequests = completedVideoGenerationResultsQuery.data?.results ?? [];

  if (!completedVideoGenerationRequests.length) {
    return null;
  }

  return (
    <DialogTrigger>
      <Button
        className={css({
          all: "unset",
          cursor: "pointer",
          display: "inline-flex",
          position: "relative",
          justifyContent: "center",
          alignItems: "center",
          border: `2px solid {colors.trellisPalette.4}`,
          borderRadius: "999px",
          padding: "small",
          color: "trellisPalette.4",
          fontSize: "1.25rem",
        })}
        aria-label={t(($) => $.advertising["Video generation requests completed"])}
      >
        <FaVideo />
        <span
          className={css({
            position: "absolute",
            top: "-4px",
            right: "-4px",
            borderRadius: "999px",
            minWidth: "18px",
            height: "18px",
            paddingX: "4px",
            color: "white",
            fontSize: "10px",
            fontWeight: 700,
            lineHeight: "18px",
            textAlign: "center",
            backgroundColor: "trellisPalette.4",
          })}
        >
          {completedVideoGenerationRequests.length}
        </span>
      </Button>
      <Popover>
        <Dialog
          className={css({
            border: "1px solid",
            borderColor: "border.default",
            borderRadius: "medium",
            width: "360px",
            padding: "medium",
            backgroundColor: "white",
            boxShadow: "rgba(0, 0, 0, 0.12) 0px 8px 24px",
          })}
        >
          <VStack gap="small">
            <span className={css({ fontSize: "medium" })}>
              {t(($) => $.advertising["The following video generation requests have been successfully completed:"])}
            </span>
            <GridList
              className={css({
                display: "grid",
                gap: "small",
                width: "100%",
                marginTop: "small",
              })}
              aria-label={t(($) => $.advertising["Completed video generation requests"])}
              items={completedVideoGenerationRequests}
            >
              {(request) => {
                const { task, completed_at } = request;
                const completionTime = moment(completed_at).format("h:mm A");
                const completionDate = dateFmt.format(moment(completed_at).toDate());
                const deadlineToRegisterAsset = moment(completed_at).add(TIME_TO_REGISTER_ASSETS, "hours");

                const remainingTimeToRegister = moment.duration(deadlineToRegisterAsset.diff(moment()));

                return (
                  <GridListItem id={request.id} className={css({ borderTop: "1px solid grey", paddingY: "small" })}>
                    <h4 className={css({ margin: 0, fontSize: "small", fontWeight: "medium" })}>{task.name}</h4>
                    <span className={css({ display: "block", paddingY: "xsmall", fontSize: "xsmall" })}>
                      {t(
                        ($) =>
                          $.advertising["Completed at {time} on {date} ({hours}hr {minutes}m left to register assets)"],
                        {
                          time: completionTime,
                          date: completionDate,
                          hours: remainingTimeToRegister.hours(),
                          minutes: remainingTimeToRegister.minutes(),
                        },
                      )}
                    </span>
                    <Link
                      className={css({
                        color: "trellisPalette.5",
                        textDecoration: "underline",
                        fontWeight: 600,
                      })}
                      to="/user/merchandising/product/$product"
                      params={{ product: String(task.gvproduct.id) }}
                      search={{ tab: "video-generation" }}
                    >
                      {t(($) => $.advertising["Review Generated Assets"])}
                    </Link>
                  </GridListItem>
                );
              }}
            </GridList>
          </VStack>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
};
